import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import styled from "styled-components";
import { Button, Form } from "react-bootstrap";
import axios from "axios";
import { useUser } from "./UserContext";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import apiClient from "../Login/apiClient";

const Container = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 70vh;
  background-color: #f5f5f5;
`;

const LoginForm = styled(Form)`
  width: 500px;
  padding: 30px;
  border-radius: 10px;
  box-shadow: 0 0 10px 0 rgba(0, 0, 0, 0.1);
  background-color: white;
`;

const SpacedFormGroup = styled(Form.Group)`
  margin-bottom: 15px;
`;

const FieldHint = styled.div`
  margin-top: 6px;
  color: #6b7280;
  font-size: 0.86rem;
  line-height: 1.5;
`;

// 新たに追加したスタイル
const CheckboxContainer = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 20px;
  gap: 10px;
`;

const TermsLink = styled(Link)`
  color: #007bff;
  text-decoration: underline;
  cursor: pointer;
`;

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const MemberRegister = () => {
  const [userID, setUserID] = useState("");
  const [password, setPassword] = useState("");
  const [isAgreed, setIsAgreed] = useState(false); // 利用規約の同意状態
  const navigate = useNavigate();

  const { setUser } = useUser();

  const handleSubmit = (event) => {
    event.preventDefault();
    // 利用規約に同意していない場合は処理を進めない
    if (!isAgreed) return;
    register(userID, password);
  };

  const register = async (userID, password) => {
    let loginResponse = null;
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/accounts/register/`,
        {
          username: userID,
          password: password,
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      console.log("ユーザ登録の成功確認ログ");

      if (response.status === 201) {
        console.log("ユーザー登録成功。次にログインを実行します。");

        // ここでログインAPIを呼び出してトークンを取得
        loginResponse = await axios.post(`${API_BASE_URL}/api/accounts/login/`, {
          username: userID,
          password: password,
        });

        const { access, refresh, user_id, username } = loginResponse.data;
        setUser({ id: user_id, userID, username: username || userID });
        apiClient.setToken(access, refresh);

        const recoveryRes = await apiClient.post("/api/accounts/recovery-codes/generate/");
        const firstCode = recoveryRes.data.recovery_code ?? "";

        console.log(firstCode);

        // 遷移先を「秘密の質問登録画面」に変更し、必要なデータを渡す
        navigate("/MemberSecretRegisterView", {
          state: {
            recoveryCode: firstCode,
            userInfoDetails: { userID, password }, // 前の画面で想定していた引継ぎデータ
          },
        });
      } else {
        toast.error("ユーザー登録は成功しましたが、ログイン処理時に予期せぬエラーが発生しました");
        console.error("ユーザー登録は成功しましたが、予期せぬレスポンスです:", response);
      }
    } catch (error) {
      const status = error?.response?.status;
      const detail = error?.response?.data; // DRF ならここに具体エラーが来ることが多い

      // どのAPIで落ちたか識別
      const phase = loginResponse ? "login" : "register";
      if (status === 400) {
        // 例: バリデーション NG / 既存ユーザー / 認証失敗など
        toast.info(typeof detail === "string" ? detail : JSON.stringify(detail ?? { message: "不正な入力です。" }));
      } else if (status === 401) {
        toast.error("認証に失敗しました。ユーザーID/パスワードをご確認ください。");
      } else if (status === 409) {
        toast.info("すでに同じユーザーが存在します。");
      } else if (status === 500) {
        toast.error("サーバエラーが発生しました。しばらくしてから再度お試しください。");
      } else {
        toast.error("予期せぬエラーが発生しました。");
      }

      console.error(`[${phase}] API error`, {
        status,
        detail,
        url: error?.config?.url,
        data: error?.config?.data,
        headers: error?.config?.headers,
      });
      console.error("登録またはログインエラー:", error.response || error);
    }
  };

  const handleUserIDChange = (event) => {
    setUserID(event.target.value);
  };

  const handlePasswordChange = (event) => {
    setPassword(event.target.value);
  };

  return (
    <React.StrictMode>
      <Container>
        <LoginForm onSubmit={handleSubmit}>
          <SpacedFormGroup controlId="formBasicUserID">
            <Form.Label>ユーザーID</Form.Label>
            {/* type="userID" から標準的な type="text" へ修正 */}
            <Form.Control
              type="text"
              placeholder="例：mubo-taro"
              value={userID}
              onChange={handleUserIDChange}
              required
            />
            <FieldHint>
              例：mubo-taro。ログイン用のIDとして使います。
            </FieldHint>
          </SpacedFormGroup>
          {/* HTML構造に基づきニックネームの注記を追加 */}
          <h6>本サイトにおけるニックネームとして表示されます</h6>
          <br />

          <SpacedFormGroup controlId="formBasicPassword">
            <Form.Label>パスワード</Form.Label>
            {/* プレースホルダーを実際の画面に合わせました */}
            <Form.Control type="password" placeholder="パスワード(8文字以上・半角英数字)" value={password} onChange={handlePasswordChange} required />
          </SpacedFormGroup>

          {/* 利用規約のチェックボックスを追加 */}
          <CheckboxContainer>
            <div>
              <Form.Check id="agree-checkbox" type="checkbox" checked={isAgreed} onChange={(e) => setIsAgreed(e.target.checked)} />
            </div>
            <span>
              <TermsLink to="/TermsOfService">利用規約</TermsLink>に同意します。
            </span>
          </CheckboxContainer>

          {/* ボタンの活性/非活性および色の動的制御を追加 */}
          <Button
            variant="primary"
            type="submit"
            size="lg"
            disabled={!isAgreed}
            className="w-100 text-center d-flex align-items-center justify-content-center"
            style={{
              minWidth: "120px",
              whiteSpace: "nowrap",
              fontSize: "1.2rem",
              backgroundColor: isAgreed ? "#282c34" : "#cccccc",
              color: "#ffffff",
              borderColor: isAgreed ? "#282c34" : "#cccccc",
            }}
          >
            会員登録
          </Button>
        </LoginForm>
        <ToastContainer position="top-center" autoClose={3000} />
      </Container>
    </React.StrictMode>
  );
};

export default MemberRegister;
